<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import subDays from 'date-fns/subDays';
import BuzzReportFilters from './components/BuzzReportFilters.vue';
import ReportHeader from './components/ReportHeader.vue';
import VenueBuzzReportCard from './components/VenueBuzzReportCard.vue';
import V4Button from 'dashboard/components-next/button/Button.vue';
import VenuesAPI from 'dashboard/api/venues';
import {
  hasBuzzScore,
  isMissingBuzzScoreError,
} from './components/buzzReportHelpers';
import { downloadBuzzReport } from './components/buzzReportDownload';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const { t } = useI18n();
const store = useStore();
const currentUser = useMapGetter('getCurrentUser');
const route = useRoute();
const router = useRouter();
const { isCloudFeatureEnabled } = useAccount();
const isBuzzEnabled = computed(() => isCloudFeatureEnabled(FEATURE_FLAGS.BUZZ));

const selectedVenueId = ref('');
const buzz = ref(null);
const peerVenues = ref([]);
const loading = ref(false);
const peersLoading = ref(false);
const refreshing = ref(false);
const error = ref('');
const noScore = ref(false);

const isSuperAdmin = computed(() => currentUser.value?.type === 'SuperAdmin');

const venues = computed(() =>
  [...(store.getters['venues/getVenues'] || [])].sort((a, b) =>
    (a.name || '').localeCompare(b.name || '')
  )
);
const venuesLoading = computed(() => store.state.venues.uiFlags.isFetching);

const venueOptions = computed(() => venues.value);

const selectedVenue = computed(
  () => venues.value.find(venue => venue.id === selectedVenueId.value) || null
);

const liveBuzzScore = () => {
  const value =
    buzz.value?.buzz_score ?? buzz.value?.summary?.b ?? buzz.value?.b;
  if (value == null || value === '' || Number.isNaN(Number(value))) {
    return null;
  }
  return Number(value);
};

const syncSelectedVenuePeer = () => {
  const venueId = selectedVenueId.value;
  const score = liveBuzzScore();
  if (!venueId || score == null) return;

  const entry = {
    venue_id: venueId,
    name: selectedVenue.value?.name || '—',
    buzz_score: score,
    buzz_score_avg_28d:
      buzz.value?.buzz_score_avg_28d ?? buzz.value?.summary?.bavg_28d ?? null,
  };
  const peers = [...peerVenues.value];
  const index = peers.findIndex(peer => peer.venue_id === venueId);
  if (index >= 0) {
    peers[index] = { ...peers[index], ...entry };
  } else {
    peers.push(entry);
  }
  peerVenues.value = peers;
};

const peerScores = computed(() =>
  peerVenues.value
    .map(peer => peer.buzz_score)
    .filter(score => score != null && !Number.isNaN(Number(score)))
    .map(score => Number(score))
);

const historyRange = () => {
  const to = new Date();
  const from = subDays(to, 90);
  return { from: from.toISOString(), to: to.toISOString() };
};

const persistVenueQuery = venueId => {
  if (!venueId || route.query.venue_id === venueId) return;
  router.replace({ query: { ...route.query, venue_id: venueId } });
};

const resolveVenueId = () => {
  const fromQuery = route.query.venue_id;
  if (fromQuery && venues.value.some(venue => venue.id === fromQuery)) {
    return fromQuery;
  }
  return venues.value[0]?.id || '';
};

const loadBuzzPeers = async () => {
  peersLoading.value = true;
  try {
    const response = await VenuesAPI.getBuzzPeers();
    peerVenues.value = response.data?.peers || [];
    syncSelectedVenuePeer();
  } catch {
    peerVenues.value = [];
  } finally {
    peersLoading.value = false;
  }
};

const loadVenueBuzz = async venueId => {
  if (!venueId) {
    buzz.value = null;
    error.value = '';
    noScore.value = false;
    loading.value = false;
    return;
  }

  loading.value = true;
  error.value = '';
  noScore.value = false;
  try {
    const response = await VenuesAPI.getBuzz(venueId, historyRange());
    buzz.value = response.data;
    noScore.value = !hasBuzzScore(response.data);
    syncSelectedVenuePeer();
  } catch (loadError) {
    if (isMissingBuzzScoreError(loadError)) {
      buzz.value = { history: [], mentions: [] };
      noScore.value = true;
      error.value = '';
    } else {
      noScore.value = false;
      error.value =
        loadError?.response?.data?.error ||
        loadError?.message ||
        t('BUZZ_REPORTS.LOAD_ERROR');
      buzz.value = null;
    }
  } finally {
    loading.value = false;
  }
};

watch(selectedVenueId, (venueId, previousId) => {
  if (!venueId) {
    selectedVenueId.value = previousId || venues.value[0]?.id || '';
    return;
  }
  persistVenueQuery(venueId);
  loadVenueBuzz(venueId);
});

watch(
  () => route.query.venue_id,
  venueId => {
    if (venueId && venueId !== selectedVenueId.value) {
      selectedVenueId.value = venueId;
    }
  }
);

onMounted(async () => {
  if (!isBuzzEnabled.value) {
    router.replace({ name: 'account_overview_reports' });
    return;
  }

  await store.dispatch('venues/get');
  await loadBuzzPeers();
  selectedVenueId.value = resolveVenueId();
});

const downloadReport = () => {
  if (!selectedVenue.value) return;
  downloadBuzzReport({ venue: selectedVenue.value, buzz: buzz.value });
};

const refreshBuzz = async () => {
  if (!selectedVenueId.value || refreshing.value) return;
  refreshing.value = true;
  try {
    await VenuesAPI.refreshBuzz(selectedVenueId.value);
    useAlert(t('BUZZ_REPORTS.REFRESH_QUEUED'));
  } catch (refreshError) {
    useAlert(
      refreshError?.response?.data?.error ||
        refreshError?.message ||
        t('BUZZ_REPORTS.REFRESH_ERROR')
    );
  } finally {
    refreshing.value = false;
  }
};
</script>

<template>
  <div v-if="isBuzzEnabled">
    <ReportHeader
      :header-title="t('BUZZ_REPORTS.HEADER')"
      :header-description="t('BUZZ_REPORTS.DESCRIPTION')"
    >
      <div class="flex items-center gap-2">
        <V4Button
          v-if="isSuperAdmin"
          :label="t('BUZZ_REPORTS.REFRESH')"
          icon="i-lucide-refresh-cw"
          size="sm"
          color="slate"
          variant="outline"
          :disabled="!selectedVenue || refreshing"
          :is-loading="refreshing"
          @click="refreshBuzz"
        />
        <V4Button
          :label="t('BUZZ_REPORTS.DOWNLOAD')"
          icon="i-ph-download-simple"
          size="sm"
          :disabled="!selectedVenue"
          @click="downloadReport"
        />
      </div>
    </ReportHeader>
    <div class="flex flex-col">
      <BuzzReportFilters
        v-if="venueOptions.length"
        v-model="selectedVenueId"
        :venues="venueOptions"
      />
      <div class="flex flex-col gap-4 pb-6 mt-4">
        <p v-if="venuesLoading || peersLoading" class="text-n-slate-11">
          {{ t('BUZZ_REPORTS.LOADING') }}
        </p>
        <p v-else-if="!venues.length" class="text-n-slate-11">
          {{ t('BUZZ_REPORTS.EMPTY') }}
        </p>
        <VenueBuzzReportCard
          v-else-if="selectedVenue"
          :key="selectedVenue.id"
          :venue="selectedVenue"
          :buzz="buzz"
          :peer-scores="peerScores"
          :loading="loading"
          :no-score="noScore"
          :error="error"
        />
      </div>
    </div>
  </div>
</template>
